import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';
import { WeeklyHours } from './BranchHoursSection';
import { useBranches } from './BranchesContext';
import { useMenus, catalogItems, CatalogItem } from './MenusContext';
export type Outlet = {
  id: string;
  branchId: string; // parent branch
  name: string;
  description?: string;
  active: boolean;
  color: string; // tailwind color name
  icon?: string; // optional icon key
  defaultMenuId: string; // references a Menu.id from MenusContext
  stationIds: string[]; // subset of parent branch's stations this outlet uses
  hoursOverride?: WeeklyHours | null;
  branding?: {
    logoUrl?: string;
    primaryColor?: string; // hex color
    heroImage?: string;
  };
};
type Ctx = {
  outlets: Outlet[];
  outletId: string | null; // active outlet
  setOutletId: (id: string) => void;
  addOutlet: (branchId: string, partial?: Partial<Outlet>) => string;
  updateOutlet: (id: string, patch: Partial<Outlet>) => void;
  deleteOutlet: (id: string) => void;
  getOutletsForBranch: (branchId: string) => Outlet[];
  getOutletMenu: (outletId: string) => CatalogItem[]; // resolver
};
const OutletsContext = createContext<Ctx | null>(null);
const SEED_OUTLETS: Outlet[] = [{
  id: 'o_main',
  branchId: 'b_main',
  name: 'Main Dining',
  description: 'Full-service restaurant',
  active: true,
  color: 'orange',
  icon: 'utensils',
  defaultMenuId: 'm1',
  stationIds: ['food', 'cold', 'dessert']
}, {
  id: 'o_bar',
  branchId: 'b_main',
  name: 'Skylight Bar',
  description: 'Cocktails & small plates',
  active: true,
  color: 'blue',
  icon: 'martini',
  defaultMenuId: 'm1',
  stationIds: ['bar']
}, {
  id: 'o_asian',
  branchId: 'b_main',
  name: 'Asian Fusion',
  description: 'Pan-Asian specialties',
  active: true,
  color: 'emerald',
  icon: 'utensils',
  defaultMenuId: 'm1',
  stationIds: ['food']
}];
export function OutletsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [outlets, setOutlets] = useState<Outlet[]>(SEED_OUTLETS);
  const {
    branchId
  } = useBranches();
  const {
    menus,
    branchOverrides
  } = useMenus();
  // Track active outlet, defaulting to first outlet of active branch
  const [outletId, setOutletIdState] = useState<string | null>(null);
  useEffect(() => {
    if (branchId) {
      const branchOutlets = outlets.filter(o => o.branchId === branchId);
      if (branchOutlets.length > 0 && (!outletId || !branchOutlets.find(o => o.id === outletId))) {
        setOutletIdState(branchOutlets[0].id);
      }
    }
  }, [branchId, outlets, outletId]);
  const setOutletId = (id: string) => {
    if (outlets.find(o => o.id === id)) {
      setOutletIdState(id);
    }
  };
  const addOutlet = (branchId: string, partial?: Partial<Outlet>) => {
    const id = `o_${Date.now().toString(36)}`;
    const newOutlet: Outlet = {
      id,
      branchId,
      name: 'New Outlet',
      active: true,
      color: 'gray',
      defaultMenuId: menus[0]?.id || '',
      stationIds: [],
      ...partial
    };
    setOutlets(prev => [...prev, newOutlet]);
    return id;
  };
  const updateOutlet = (id: string, patch: Partial<Outlet>) => {
    setOutlets(prev => prev.map(o => o.id === id ? {
      ...o,
      ...patch
    } : o));
  };
  const deleteOutlet = (id: string) => {
    setOutlets(prev => prev.filter(o => o.id !== id));
  };
  const getOutletsForBranch = (bId: string) => {
    return outlets.filter(o => o.branchId === bId);
  };
  const getOutletMenu = (oId: string) => {
    const outlet = outlets.find(o => o.id === oId);
    if (!outlet) return [];
    const menu = menus.find(m => m.id === outlet.defaultMenuId);
    if (!menu) return [];
    const overrides = branchOverrides[outlet.branchId] || {};
    return catalogItems.filter(item => menu.itemIds.includes(item.id)) // in menu
    .filter(item => {
      // available at branch
      const o = overrides[item.id];
      return o?.available ?? true;
    }).filter(item => {
      // station matches outlet stations
      return outlet.stationIds.includes(item.stationId);
    }).map(item => {
      // apply branch price
      const o = overrides[item.id];
      return {
        ...item,
        price: o?.price ?? item.price
      };
    });
  };
  return <OutletsContext.Provider value={{
    outlets,
    outletId,
    setOutletId,
    addOutlet,
    updateOutlet,
    deleteOutlet,
    getOutletsForBranch,
    getOutletMenu
  }} data-id="element-3008">
      {children}
    </OutletsContext.Provider>;
}
export function useOutlets() {
  const ctx = useContext(OutletsContext);
  if (!ctx) throw new Error('useOutlets must be used within OutletsProvider');
  return ctx;
}